import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Email as EmailIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useQuery, useMutation } from '@apollo/client';
import { GET_USERS_BY_COMPANY } from '../graphql/queries';
import { INVITE_COMPANY_USER, ACTIVATE_USER, DEACTIVATE_USER, RESEND_WELCOME_EMAIL, UPDATE_USER_EMAIL } from '../graphql/mutations';

const UsersTab = ({ companyId }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
  });
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');

  const { data, loading, error, refetch } = useQuery(GET_USERS_BY_COMPANY, {
    variables: { companyId },
    fetchPolicy: 'network-only',
  });

  const [inviteUser, { loading: inviting }] = useMutation(INVITE_COMPANY_USER, {
    onCompleted: (result) => {
      if (result.inviteCompanyUser.success) {
        setSuccessMessage(result.inviteCompanyUser.message);
        setDialogOpen(false);
        setFormData({ email: '', firstName: '', lastName: '' });
        refetch();
      } else {
        setErrors({ general: result.inviteCompanyUser.message });
      }
    },
    onError: (err) => {
      setErrors({ general: err.message });
    },
  });

  const [activateUser] = useMutation(ACTIVATE_USER, {
    onCompleted: () => refetch(),
    onError: (err) => setErrors({ general: err.message }),
  });

  const [deactivateUser] = useMutation(DEACTIVATE_USER, {
    onCompleted: () => refetch(),
    onError: (err) => setErrors({ general: err.message }),
  });

  const [resendWelcomeEmail, { loading: resending }] = useMutation(RESEND_WELCOME_EMAIL, {
    onCompleted: (result) => {
      if (result.resendWelcomeEmail.success) {
        setSuccessMessage(result.resendWelcomeEmail.message);
      } else {
        setErrors({ general: result.resendWelcomeEmail.message });
      }
    },
    onError: (err) => setErrors({ general: err.message }),
  });

  const [updateUserEmail, { loading: updatingEmail }] = useMutation(UPDATE_USER_EMAIL, {
    onCompleted: (result) => {
      if (result.updateUserEmail.success) {
        setSuccessMessage(result.updateUserEmail.message);
        setEmailDialogOpen(false);
        setSelectedUser(null);
        setNewEmail('');
        refetch();
      } else {
        setErrors({ general: result.updateUserEmail.message });
      }
    },
    onError: (err) => setErrors({ general: err.message }),
  });

  const users = data?.usersByCompany || [];

  const handleOpenDialog = () => {
    setDialogOpen(true);
    setErrors({});
    setSuccessMessage('');
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData({ email: '', firstName: '', lastName: '' });
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email.trim()) {
      newErrors.email = 'Email е задължителен';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Невалиден email адрес';
    }
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Името е задължително';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Фамилията е задължителна';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    await inviteUser({
      variables: {
        input: {
          companyId,
          email: formData.email.trim(),
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
        },
      },
    });
  };

  const handleToggleActive = async (user) => {
    if (user.isActive) {
      await deactivateUser({ variables: { userId: user.id } });
    } else {
      await activateUser({ variables: { userId: user.id } });
    }
  };

  const handleResendEmail = async (user) => {
    await resendWelcomeEmail({ variables: { userId: user.id } });
  };

  const handleOpenEmailDialog = (user) => {
    setSelectedUser(user);
    setNewEmail(user.email);
    setEmailDialogOpen(true);
    setErrors({});
  };

  const handleCloseEmailDialog = () => {
    setEmailDialogOpen(false);
    setSelectedUser(null);
    setNewEmail('');
    setErrors({});
  };

  const handleUpdateEmail = async () => {
    if (!newEmail.trim()) {
      setErrors({ general: 'Email е задължителен' });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      setErrors({ general: 'Невалиден email адрес' });
      return;
    }
    await updateUserEmail({
      variables: { userId: selectedUser.id, newEmail: newEmail.trim() },
    });
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'ADMIN':
        return 'Администратор';
      case 'USER':
        return 'Потребител';
      case 'ACCOUNTANT':
        return 'Счетоводител';
      default:
        return role;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'ADMIN':
        return 'primary';
      case 'USER':
        return 'default';
      case 'ACCOUNTANT':
        return 'secondary';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Грешка при зареждане: {error.message}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Потребители на фирмата</Typography>
        <Box>
          <IconButton onClick={() => refetch()} sx={{ mr: 1 }}>
            <RefreshIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={handleOpenDialog}
          >
            Добави потребител
          </Button>
        </Box>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      {errors.general && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrors({})}>
          {errors.general}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Име</TableCell>
              <TableCell>Потребителско име</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Роля</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell align="right">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Няма потребители
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    {user.firstName && user.lastName
                      ? `${user.firstName} ${user.lastName}`
                      : '-'}
                  </TableCell>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={getRoleLabel(user.role)}
                      color={getRoleColor(user.role)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.isActive ? 'Активен' : 'Неактивен'}
                      color={user.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    {user.role !== 'ADMIN' && (
                      <>
                        <Tooltip title="Изпрати имейл с нова парола">
                          <IconButton
                            onClick={() => handleResendEmail(user)}
                            color="primary"
                            disabled={resending}
                            size="small"
                          >
                            <EmailIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Промени email">
                          <IconButton
                            onClick={() => handleOpenEmailDialog(user)}
                            color="default"
                            size="small"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={user.isActive ? 'Деактивирай' : 'Активирай'}>
                          <IconButton
                            onClick={() => handleToggleActive(user)}
                            color={user.isActive ? 'error' : 'success'}
                            size="small"
                          >
                            {user.isActive ? <CancelIcon /> : <CheckCircleIcon />}
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog for adding new user */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Добави нов потребител</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {errors.general && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errors.general}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              error={!!errors.email}
              helperText={errors.email}
              margin="normal"
              required
            />

            <TextField
              fullWidth
              label="Име"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              error={!!errors.firstName}
              helperText={errors.firstName}
              margin="normal"
              required
            />

            <TextField
              fullWidth
              label="Фамилия"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              error={!!errors.lastName}
              helperText={errors.lastName}
              margin="normal"
              required
            />

            <Alert severity="info" sx={{ mt: 2 }}>
              Потребителското име и парола ще бъдат генерирани автоматично и изпратени на посочения email.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Отказ</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={inviting}
          >
            {inviting ? <CircularProgress size={20} /> : 'Добави'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for changing email */}
      <Dialog open={emailDialogOpen} onClose={handleCloseEmailDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Промяна на email адрес</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {errors.general && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errors.general}
              </Alert>
            )}

            <Typography variant="body2" sx={{ mb: 2 }}>
              Потребител: <strong>{selectedUser?.firstName} {selectedUser?.lastName}</strong> ({selectedUser?.username})
            </Typography>

            <TextField
              fullWidth
              label="Нов email адрес"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              margin="normal"
              required
            />

            <Alert severity="info" sx={{ mt: 2 }}>
              След промяната, използвайте бутона за изпращане на имейл, за да изпратите данните за достъп на новия адрес.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEmailDialog}>Отказ</Button>
          <Button
            onClick={handleUpdateEmail}
            variant="contained"
            disabled={updatingEmail}
          >
            {updatingEmail ? <CircularProgress size={20} /> : 'Запази'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersTab;
